import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, TextField, Button, Checkbox, FormControlLabel,
  Stack, Alert, MenuItem, Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  createPixIntegration,
  getPixIntegrationById,
  updatePixIntegration,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper',
  },
};

function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...zen.sectionCardSx, ...sx }}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

const PSP_LIST = [
  { value: 0, label: 'Bradesco' },
  { value: 1, label: 'Itaú' },
  { value: 2, label: 'Banco do Brasil' },
  { value: 3, label: 'Santander' },
  { value: 4, label: 'Shipay' },
  { value: 5, label: 'Sicredi' },
  { value: 6, label: 'Sicoob' },
  { value: 7, label: 'PagSeguro' },
  { value: 8, label: 'Gerencianet' },
  { value: 9, label: 'PixPDV' },
  { value: 10, label: 'Inter' },
  { value: 11, label: 'Ailos' },
  { value: 12, label: 'Matera' },
  { value: 13, label: 'Cielo' },
  { value: 14, label: 'MercadoPago' },
  { value: 18, label: 'AppLess' },
];

const TIPO_CHAVE = [
  { value: 0, label: 'Nenhuma' },
  { value: 1, label: 'Email' },
  { value: 2, label: 'CPF' },
  { value: 3, label: 'CNPJ' },
  { value: 4, label: 'Celular' },
  { value: 5, label: 'Chave Aleatória' },
];

export default function FormPixIntegration({
  embedded = false,
  pixIntegrationId = null,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const id = pixIntegrationId ?? params.id;
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    id: 0,
    ativo: true,
    descricao: '',
    psp: 0,
    ambiente: 0,
    tipoChave: 0,
    chavePix: '',
    clientId: '',
    clientSecret: '',
    accessToken: '',
    token: '',
    secretKey: '',
    accessKey: '',
    senhaCertificado: '',
    pathCertificado: '',
    pathCertificadoRoot: '',
    pathChavePrivada: '',
    scopes: '',
    nomeRecebedor: '',
    cidadeRecebedor: '',
    ufRecebedor: '',
    cepRecebedor: '',
    timeout: '',
    expiracaoQrCode: 900,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const titulo = useMemo(
    () => (isEditMode ? `Editar Integração PIX #${id}` : 'Nova Integração PIX'),
    [isEditMode, id]
  );

  useEffect(() => {
    (async () => {
      if (!isEditMode) return;
      try {
        const { data } = await getPixIntegrationById(id);
        setForm({
          id: data.id,
          ativo: data.ativo !== false,
          descricao: data.descricao || '',
          psp: Number(data.psp ?? 0),
          ambiente: Number(data.ambiente ?? 0),
          tipoChave: Number(data.tipoChave ?? 0),
          chavePix: data.chavePix || '',
          clientId: data.clientId || '',
          clientSecret: data.clientSecret || '',
          accessToken: data.accessToken || '',
          token: data.token || '',
          secretKey: data.secretKey || '',
          accessKey: data.accessKey || '',
          senhaCertificado: data.senhaCertificado || '',
          pathCertificado: data.pathCertificado || '',
          pathCertificadoRoot: data.pathCertificadoRoot || '',
          pathChavePrivada: data.pathChavePrivada || '',
          scopes: data.scopes || '',
          nomeRecebedor: data.nomeRecebedor || '',
          cidadeRecebedor: data.cidadeRecebedor || '',
          ufRecebedor: data.ufRecebedor || '',
          cepRecebedor: data.cepRecebedor || '',
          timeout: data.timeout ?? '',
          expiracaoQrCode: Number(data.expiracaoQrCode ?? 900),
        });
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Não foi possível carregar a integração PIX.'));
      }
    })();
  }, [isEditMode, id]);

  const onChange = (field) => (e) => {
    const isCheckbox = e.target.type === 'checkbox';
    const val = isCheckbox ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/pix-integrations');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSaving(true);
    try {
      if (!form.descricao.trim()) throw new Error('Descrição é obrigatória.');

      const payload = {
        id: isEditMode ? Number(id) : 0,
        ativo: !!form.ativo,
        descricao: form.descricao.trim(),
        psp: Number(form.psp),
        ambiente: Number(form.ambiente),
        tipoChave: Number(form.tipoChave),
        chavePix: form.chavePix || null,
        clientId: form.clientId || null,
        clientSecret: form.clientSecret || null,
        accessToken: form.accessToken || null,
        token: form.token || null,
        secretKey: form.secretKey || null,
        accessKey: form.accessKey || null,
        senhaCertificado: form.senhaCertificado || null,
        pathCertificado: form.pathCertificado || null,
        pathCertificadoRoot: form.pathCertificadoRoot || null,
        pathChavePrivada: form.pathChavePrivada || null,
        scopes: form.scopes || null,
        nomeRecebedor: form.nomeRecebedor || null,
        cidadeRecebedor: form.cidadeRecebedor || null,
        ufRecebedor: form.ufRecebedor || null,
        cepRecebedor: form.cepRecebedor || null,
        timeout: form.timeout === '' ? null : Number(form.timeout),
        expiracaoQrCode: Number(form.expiracaoQrCode) || 900,
      };

      if (isEditMode) await updatePixIntegration(Number(id), payload);
      else await createPixIntegration(payload);

      if (onSaved) {
        onSaved(isEditMode ? 'Integração Pix atualizada' : 'Integração Pix criada');
      } else {
        navigate('/pix-integrations');
      }
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar a integração Pix.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 1100, mx: 'auto' }}>
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          {titulo}
        </Typography>
      )}

      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{String(errorMessage)}</Alert>}

      <Section title="Geral" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Descrição" value={form.descricao} onChange={onChange('descricao')} fullWidth required />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="PSP" select value={form.psp} onChange={onChange('psp')} fullWidth required>
              {PSP_LIST.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Ambiente" select value={form.ambiente} onChange={onChange('ambiente')} fullWidth>
              <MenuItem value={0}>Homologação</MenuItem>
              <MenuItem value={1}>Produção</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Tipo de chave" select value={form.tipoChave} onChange={onChange('tipoChave')} fullWidth>
              {TIPO_CHAVE.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField label="Chave PIX" value={form.chavePix} onChange={onChange('chavePix')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Checkbox checked={form.ativo} onChange={onChange('ativo')} />} label="Ativo" />
          </Grid>
        </Grid>
      </Section>

      <Section title="Credenciais" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Client ID" value={form.clientId} onChange={onChange('clientId')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Client Secret" value={form.clientSecret} onChange={onChange('clientSecret')} fullWidth type="password" autoComplete="new-password" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Token" value={form.token} onChange={onChange('token')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Access Token" value={form.accessToken} onChange={onChange('accessToken')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Secret Key" value={form.secretKey} onChange={onChange('secretKey')} fullWidth type="password" autoComplete="new-password" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Access Key" value={form.accessKey} onChange={onChange('accessKey')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Scopes" value={form.scopes} onChange={onChange('scopes')} fullWidth helperText="Separados por espaço, se o PSP exigir" />
          </Grid>
        </Grid>
      </Section>

      <Section title="Recebedor / QR Code" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Nome recebedor" value={form.nomeRecebedor} onChange={onChange('nomeRecebedor')} fullWidth />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Cidade" value={form.cidadeRecebedor} onChange={onChange('cidadeRecebedor')} fullWidth />
          </Grid>
          <Grid item xs={12} md={1}>
            <TextField label="UF" value={form.ufRecebedor} onChange={onChange('ufRecebedor')} fullWidth inputProps={{ maxLength: 2 }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="CEP" value={form.cepRecebedor} onChange={onChange('cepRecebedor')} fullWidth />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Timeout (s)" type="number" value={form.timeout} onChange={onChange('timeout')} fullWidth />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField label="Expiração QR (s)" type="number" value={form.expiracaoQrCode} onChange={onChange('expiracaoQrCode')} fullWidth />
          </Grid>
        </Grid>
      </Section>

      <Section title="Certificados (caminhos no PDV)" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Path certificado" value={form.pathCertificado} onChange={onChange('pathCertificado')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Path certificado root" value={form.pathCertificadoRoot} onChange={onChange('pathCertificadoRoot')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Path chave privada" value={form.pathChavePrivada} onChange={onChange('pathChavePrivada')} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Senha do certificado" value={form.senhaCertificado} onChange={onChange('senhaCertificado')} fullWidth type="password" autoComplete="new-password" />
          </Grid>
        </Grid>
      </Section>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={1} justifyContent="center">
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          startIcon={<SaveIcon />}
          sx={{ minWidth: 160, background: zen.blue, '&:hover': { background: zen.blueDark }, textTransform: 'none', fontWeight: 700 }}
        >
          {isEditMode ? 'Salvar Alterações' : 'Cadastrar'}
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={fechar}
          startIcon={<CancelIcon />}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Cancelar
        </Button>
      </Stack>
    </Box>
  );
}
